import { type CSSProperties, forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../lib/useReducedMotion";
import { releaseWillChange } from "./release-will-change.js";

/**
 * HeroReveal, the once-per-mount hero choreography.
 *
 * Wraps a hero block (greeting, ThriveScore, ops ROI card) with the
 * `hero-reveal` keyframe: opacity 0→1 + translateY 8px→0 + blur 4px→0
 * over 320ms with `--ease-out-expo`.
 *
 * Validated against the apps that actually ship calm-app motion in
 * 2026 (Linear, Vercel, Stripe Dashboard, Apple Health Summary, Arc
 * command bar). The mask-image gradient reveal pattern from Once UI's
 * `RevealFx` reads as agency work in an SPA context - this is the
 * calmer, more app-shaped equivalent.
 *
 * Fires once per mount. Subsequent re-renders do not retrigger; the
 * keyframe runs to completion and the element settles into its final
 * state. To replay (e.g. after a route change), unmount and remount -
 * usually achieved via React's `key` prop.
 *
 * `prefers-reduced-motion: reduce`: skips the animation entirely and
 * renders the children in their final state. This is enforced both by
 * the global reduced-motion block in `animations.css` (which neutralises
 * all keyframes) AND by the hook below as a defence-in-depth - the hook
 * also avoids attaching `will-change`, which is the meaningful saving.
 */

export interface HeroRevealProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Optional delay before the reveal fires, e.g. for sequencing the
   * greeting before the score card. Use sparingly - long delays push
   * the perceived load time.
   */
  delay?: number;
  /**
   * Render as an inline element (`display: inline-block`) instead of
   * the default block. Useful when wrapping a single inline number.
   */
  inline?: boolean;
  /**
   * When false, skips the keyframe reveal and renders children in their
   * final state - e.g. after a view-transition morph already played the
   * entrance. Defaults to true.
   */
  active?: boolean;
  children: ReactNode;
}

export const HeroReveal = forwardRef<HTMLDivElement, HeroRevealProps>(function HeroReveal(
  { delay, inline, active = true, className, style, children, onAnimationEnd, ...rest },
  ref,
) {
  const reduced = useReducedMotion();

  if (reduced || !active) {
    return (
      <div ref={ref} className={cn(inline && "inline-block", className)} style={style} {...rest}>
        {children}
      </div>
    );
  }

  const animationStyle: CSSProperties = {
    // Fallback duration is required: an undefined var() in the animation
    // shorthand silently invalidates the whole declaration (no reveal at all).
    animation: "hero-reveal var(--motion-meta-hero-reveal, 320ms) var(--ease-out-expo) both",
    willChange: "opacity, transform, filter",
    ...(delay !== undefined ? { animationDelay: `${delay}ms` } : {}),
    ...style,
  };

  return (
    <div
      ref={ref}
      className={cn(inline && "inline-block", className)}
      style={animationStyle}
      onAnimationEnd={(event) => {
        // Drop the will-change hint once the one-shot reveal completes.
        // Leaving it pinned keeps the subtree on its own compositing
        // layer, which disables subpixel text antialiasing and renders
        // the whole hero (e.g. the attention card) slightly blurry.
        releaseWillChange(event, "hero-reveal");
        onAnimationEnd?.(event);
      }}
      {...rest}
    >
      {children}
    </div>
  );
});
